import pick from 'lodash/pick';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { isUserAuthorized } from '../../util/userHelpers';
import { getProcess, resolveLatestProcessName } from '../../transactions/transaction';
import { fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

const { UUID } = sdkTypes;

const getTransitionName = processAlias => {
  const processName = resolveLatestProcessName(processAlias.split('/')[0]);
  const process = getProcess(processName);
  return process.transitions.REQUEST_QUOTE;
};

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/RequestQuotePage/SET_INITIAL_STATE';
export const SET_INITIAL_VALUES = 'RequestQuotePage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/RequestQuotePage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_SUCCESS = 'app/RequestQuotePage/SHOW_LISTING_SUCCESS';
export const SHOW_LISTING_ERROR = 'app/RequestQuotePage/SHOW_LISTING_ERROR';

export const REQUEST_QUOTE_REQUEST = 'app/RequestQuotePage/REQUEST_QUOTE_REQUEST';
export const REQUEST_QUOTE_SUCCESS = 'app/RequestQuotePage/REQUEST_QUOTE_SUCCESS';
export const REQUEST_QUOTE_ERROR = 'app/RequestQuotePage/REQUEST_QUOTE_ERROR';

// ================ Reducer ================ //

const initialState = {
  listingId: null,
  showListingInProgress: false,
  showListingError: null,
  transaction: null,
  requestQuoteInProgress: false,
  requestQuoteError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };

    case SET_INITIAL_VALUES:
      return { ...initialState, ...pick(payload, Object.keys(initialState)) };

    case SHOW_LISTING_REQUEST:
      return {
        ...state,
        listingId: payload.listingId,
        showListingError: null,
        showListingInProgress: true,
      };

    case SHOW_LISTING_SUCCESS:
      return {
        ...state,
        showListingInProgress: false,
      };

    case SHOW_LISTING_ERROR:
      return {
        ...state,
        showListingInProgress: false,
        showListingError: payload,
      };

    case REQUEST_QUOTE_REQUEST:
      return {
        ...state,
        requestQuoteError: null,
        requestQuoteInProgress: true,
      };

    case REQUEST_QUOTE_SUCCESS:
      return {
        ...state,
        requestQuoteInProgress: false,
        transaction: payload,
      };

    case REQUEST_QUOTE_ERROR:
      return {
        ...state,
        requestQuoteInProgress: false,
        requestQuoteError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({ type: SET_INITIAL_STATE });

export const setInitialValues = values => ({
  type: SET_INITIAL_VALUES,
  payload: values,
});

export const showListingRequest = listingId => ({
  type: SHOW_LISTING_REQUEST,
  payload: { listingId },
});

export const showListingSuccess = () => ({
  type: SHOW_LISTING_SUCCESS,
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const requestQuoteRequest = () => ({
  type: REQUEST_QUOTE_REQUEST,
});

export const requestQuoteSuccess = tx => ({
  type: REQUEST_QUOTE_SUCCESS,
  payload: tx,
});

export const requestQuoteError = e => ({
  type: REQUEST_QUOTE_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const showListing = (listingId, config) => (dispatch, getState, sdk) => {
  dispatch(showListingRequest(listingId));

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images'],
    'fields.image': [
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  return sdk.listings
    .show(params)
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      dispatch(addMarketplaceEntities(data, { listingFields }));
      dispatch(showListingSuccess());
      return data;
    })
    .catch(e => dispatch(showListingError(storableError(e))));
};

export const requestQuote = (params, processAlias) => (dispatch, getState, sdk) => {
  dispatch(requestQuoteRequest());

  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    dispatch(requestQuoteError(storableError(error)));
    return Promise.reject(error);
  }

  const transition = getTransitionName(processAlias);

  const bodyParams = { processAlias, transition, params };
  const queryParams = { include: ['booking', 'provider'], expand: true };

  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      const tx = entities[0];

      dispatch(fetchCurrentUserHasOrdersSuccess({ hasOrders: true }));
      dispatch(requestQuoteSuccess(tx));

      return tx;
    })
    .catch(e => {
      const err = storableError(e);
      dispatch(requestQuoteError(err));
      return Promise.reject(err);
    });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const listingId = new UUID(params.id);
  const currentUser = getState().user?.currentUser;

  dispatch(setInitialState());

  const isPrivate = config.accessControl.marketplace.private === true;
  const authorized = currentUser && isUserAuthorized(currentUser);

  const canFetch = !isPrivate || (isPrivate && authorized);

  if (!canFetch) return Promise.resolve();

  return dispatch(showListing(listingId, config));
};
